// Where the report pane's report comes from (TGCLOUD-1868, plan D1, D6): the document each kind of
// verification view hands ReportViewer, and the saved Test's anchors.
//
//   uploadDocument(review)        a report uploaded on this Test: the Blob kept on the review
//                                 (useLabReportReview.intake), nothing fetched
//   inboxDocument(itemId)         an inbox hand-off: the inbox's stored copy,
//                                 GET /test-results/inbox/:id/source/content
//   savedDocument(labReportId)    a saved Test's report: GET /test-results/:id/source/content
//   useSavedReportAnchors(id)     GET /test-results/:id/anchors: { pageCount, anchors, unanchored,
//                                 groups }, by the record's stable paths
//   openReportInNewTab(id)        the signed link (GET /test-results/:id/source) in a new tab: the
//                                 way a saved report was always opened, kept beside the view
//
// Each document has a `key`, so the pane loads it once however often the view renders, and loads
// again only for another report. Every fetch takes the pane's abort signal. A refusal asked for as
// a blob carries its JSON as a Blob too; it is read back into the error, so the server's sentence
// can be shown (as labReportInboxApi.reportBytesOf does).
import React from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

const withId = (url, id) => url.replace(':id', encodeURIComponent(id));
const isBlob = (v) => typeof Blob !== 'undefined' && v instanceof Blob;

function textOfBlob(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** The PDF bytes at `url`, as a Blob typed application/pdf. */
export async function pdfBytesAt(url, { signal } = {}) {
  try {
    const res = await axios.get(url, {
      responseType: 'blob',
      ...(signal ? { signal } : {})
    });
    return new Blob([res.data], { type: 'application/pdf' });
  } catch (err) {
    if (isBlob(err?.response?.data)) {
      try {
        err.response.data = JSON.parse(await textOfBlob(err.response.data));
      } catch (_) {
        err.response.data = {};
      }
    }
    throw err;
  }
}

/** A report uploaded in this browser: the review's own Blob, or null when it holds none. */
export function uploadDocument(review) {
  if (!review || !isBlob(review.file)) return null;
  return { kind: 'blob', blob: review.file, key: `upload:${review.key}` };
}

/** The lab report inbox's stored copy of an item's report. */
export function inboxDocument(itemId) {
  if (!itemId) return null;
  const id = String(itemId);
  return {
    kind: 'fetch',
    key: `inbox:${id}`,
    fetchBytes: ({ signal } = {}) =>
      pdfBytesAt(withId(APICONSTANTS.labReportInboxSourceContent, id), {
        signal
      })
  };
}

/** A saved Test's lab report record's stored report. */
export function savedDocument(labReportId) {
  if (!labReportId) return null;
  const id = String(labReportId);
  return {
    kind: 'fetch',
    key: `saved:${id}`,
    fetchBytes: ({ signal } = {}) =>
      pdfBytesAt(withId(APICONSTANTS.testResultSourceContent, id), { signal })
  };
}

/**
 * A saved Test's report in a new tab, by its signed link. Rejects with the request's error, or when
 * no link comes back.
 */
export async function openReportInNewTab(labReportId) {
  const { data } = await axios.get(
    withId(APICONSTANTS.testResultSource, String(labReportId))
  );
  if (!data?.fileURL) throw new Error('No link was returned.');
  window.open(data.fileURL, '_blank', 'noopener,noreferrer');
}

const NO_ANSWER = Object.freeze({ status: 'idle', answer: null, error: null });

/**
 * Where each value a saved Test's lab report record keeps as the report's is printed
 * (GET /test-results/:id/anchors). Only the answer for the id asked for is ever returned.
 *
 * @param {string|null} labReportId
 * @returns {{ status: 'idle'|'loading'|'ready'|'failed', answer: object|null, error: object|null }}
 */
export function useSavedReportAnchors(labReportId) {
  const [state, setState] = React.useState({ ...NO_ANSWER, id: null });
  React.useEffect(() => {
    if (!labReportId) {
      setState({ ...NO_ANSWER, id: null });
      return undefined;
    }
    const id = String(labReportId);
    let live = true;
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    setState({ status: 'loading', answer: null, error: null, id });
    axios
      .get(withId(APICONSTANTS.testResultAnchors, id), {
        ...(controller ? { signal: controller.signal } : {})
      })
      .then(
        ({ data }) => {
          if (live) {
            setState({ status: 'ready', answer: data || {}, error: null, id });
          }
        },
        (error) => {
          if (live) setState({ status: 'failed', answer: null, error, id });
        }
      );
    return () => {
      live = false;
      if (controller) controller.abort();
    };
  }, [labReportId]);
  return state.id === (labReportId ? String(labReportId) : null)
    ? state
    : { ...NO_ANSWER, status: labReportId ? 'loading' : 'idle' };
}
